import {
  CheckableAvatarSelectItem,
  DropdownSelect,
} from '@dixa/lib-shared-habla';
import type { ReadSearchApiArg } from 'lib-search-types';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useMemo, useState } from 'react';
import { useUpdateEffect } from 'react-use';

import { useLazyReadSearchInfiniteQuery } from 'apis/kraken';
import { useAppSearchParams } from 'utils/url/useAppSearchParams';

import { ConversationsFilterDropdownItem } from '../../conversationsFiltersToolbar.types';

/** Represents the component for filtering the conversations by contact. */
export const ConversationsContactFilter = (): JSX.Element => {
  const {
    params: { requester: requesterParam },
    unsetParams,
    patchParams,
  } = useAppSearchParams('/conversations');

  const initialArg: ReadSearchApiArg<'users'> = useMemo(
    () => ({
      scope: 'users',
      sortBy: { field: 'display_name', order: 'asc' },
      filters: {
        strategy: 'all',
        filters: [
          {
            field: 'user_type',
            operator: 'is_one',
            values: [{ value: 'contact' }],
          },
        ],
      },
    }),
    [],
  );

  const [readSearchUsers, { results: contacts, hasNextPage, fetchNextPage }] =
    useLazyReadSearchInfiniteQuery<'users'>();

  const [searchTerm, setSearchTerm] = useState<string>();

  useUpdateEffect(() => {
    readSearchUsers(
      {
        ...initialArg,
        query: searchTerm,
      },
      { debounceTime: 300 },
    );
  }, [readSearchUsers, initialArg, searchTerm]);

  const dropdownItems = useMemo<readonly ConversationsFilterDropdownItem[]>(
    () =>
      contacts.map(({ id, name, email, phone_number }) => ({
        id,
        name: name || email || phone_number || id,
      })),
    [contacts],
  );

  const selectedDropdownItemIds = useMemo(
    () =>
      typeof requesterParam !== 'undefined'
        ? typeof requesterParam === 'string'
          ? [requesterParam]
          : requesterParam
        : [],
    [requesterParam],
  );

  const handleSearch = useCallback((value?: string) => {
    setSearchTerm(value?.trim() || undefined);
  }, []);

  const handleToggle = useCallback(
    (isOpen: boolean) => {
      if (isOpen && !contacts.length) {
        readSearchUsers({
          ...initialArg,
          query: searchTerm,
        });
      } else {
        setSearchTerm(undefined);
      }
    },
    [readSearchUsers, initialArg, searchTerm, contacts.length],
  );

  const handleChange = useCallback(
    (item: ConversationsFilterDropdownItem, checked: boolean) => {
      patchParams({
        set: {
          requester: checked
            ? [...selectedDropdownItemIds, item.id]
            : selectedDropdownItemIds.filter((id) => id !== item.id),
        },
        unset: ['limit', 'offset'],
      });

      ampli.conversationsFilterToggled({
        filter: 'requester',
        value: 'id',
        checked,
      });
    },
    [patchParams, selectedDropdownItemIds],
  );

  const handleClear = useCallback(() => {
    unsetParams(['requester', 'limit', 'offset']);
    ampli.conversationsFilterCleared({ filter: 'requester' });
  }, [unsetParams]);

  return (
    <DropdownSelect
      keepOpen
      hasSearch
      noArrow={false}
      // TODO: [TEAMS-9726] Remove re-casting when fixed in Habla
      items={dropdownItems as ConversationsFilterDropdownItem[]}
      selectedItems={
        selectedDropdownItemIds as ConversationsFilterDropdownItem['id'][]
      }
      itemComponent={CheckableAvatarSelectItem}
      buttonProps={{
        variant: 'tertiary',
        label: selectedDropdownItemIds.length || undefined,
      }}
      dropdownProps={{ style: { minWidth: 280 } }}
      pagination={{
        previousEnable: false,
        nextEnable: hasNextPage,
        infinite: true,
        onNext: () => Promise.resolve(fetchNextPage()),
      }}
      onChange={handleChange}
      onClear={handleClear}
      onSearch={handleSearch}
      onToggle={handleToggle}
    >
      Contacts
    </DropdownSelect>
  );
};
